<template>
  <!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/mouse-events-have-key-events -->
  <div
    ref="messageRef"
    class="s-message"
    :class="{
      '-showContextMenu': showContextMenuTrigger || isContextMenuOpen,
      's-message--swiping': isSwiping,
      's-message--swipeActive': isPastThreshold,
      '-isReply': isReply,
    }"
    :style="{ ...swipeStyle, ...swipeResetStyle }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="onMessageClick"
    @touchstart="handleTouchStartWrapper"
    @touchmove="handleTouchMoveWrapper"
    @touchend="handleTouchEndWrapper">
    <!-- Swipe reply indicator -->
    <div
      v-if="isSwiping"
      class="s-message__swipeIndicator"
      :class="{ '-active': isPastThreshold, '-ownMessage': isOwnMessage }">
      <SvgIcon
        small
        type="reply"
        :style="{ transform: `scale(${swipeIndicatorScale})`, opacity: swipeIndicatorOpacity }" />
    </div>
    <div
      class="c-chat__message t-body3"
      :class="{
        [`-${message.metadata?.state}`]: message.metadata?.state,
        '-read': isMessageRead && isMe(message.senderDisplayName) && message.metadata?.state !== 'error',
        '-link': message.metadata?.type === 'link',
        '-imageAttachment': isImageAttachment,
        '-hasCancelButton': canCancelUpload,
        '-deleting': isDeleting,
      }">
      <MessageContextMenu
        v-if="canShowContextMenu"
        ref="contextMenuRef"
        :message="message"
        :can-delete="canDeleteMessage"
        :can-reply="canReplyToMessage"
        :is-own-message="isOwnMessage"
        @reply="handleReplyRequest"
        @delete="handleDeleteRequest" />
      <div
        v-if="message.metadata?.uploading === 'true' && !isImageAttachment"
        class="c-chat__messageUploading"
        :style="{ '--message-loader-width': message.metadata?.uploadedPercentage * 100 + '%' }">
        <button
          v-if="canCancelUpload"
          type="button"
          :aria-label="$t('chat.cancel_upload')"
          :title="$t('chat.cancel_upload')"
          class="c-chat__messageCancelUpload"
          @click.stop="handleCancelUpload">
          <SvgIcon
            type="close"
            aria-hidden="true"
            tiny />
        </button>
      </div>
      <ReplyPreview
        v-if="isReply && repliedToMessage"
        :message="repliedToMessage"
        :parent-message="message"
        :username="replyUsername"
        :is-deleted="repliedToMessage?.deleted"
        :show-cancel-button="false" />
      <template v-if="showLoaderForReceiver">
        <LoadingSpinner />
      </template>
      <template v-else>
        <template v-if="isAttachment">
          <MessageAttachment :message="message" />
          <div
            v-if="transcriptHtml"
            class="s-messageContent">
            <span
              v-safe-chat-html="transcriptHtml"
              class="s-chat__audioTranscript t-caption1 u-marginTop1" />
          </div>
        </template>
        <template v-else>
          <div class="o-flex__item -alignCenter -spaceBetween">
            <div class="s-messageContent">
              <template v-for="(part, index) in messageParts">
                <span
                  v-if="part.type === 'text'"
                  :key="'text-' + index"
                  v-safe-chat-html="part.content"
                  :class="{ 'c-chat__largeEmoji': shouldDisplayLargeEmojis }" />
                <img
                  v-else-if="part.type === 'emoji'"
                  :key="'emoji-' + index"
                  :src="part.src"
                  :alt="`:${part.id}:`"
                  class="c-emoji__image"
                  :class="{
                    '-large': shouldDisplayLargeEmojis,
                  }" />
                <KLink
                  v-else-if="part.type === 'link'"
                  :key="'link-' + index"
                  :href="part.url"
                  :user-ids="userIdsForAccessCheck"
                  class="c-chat__messageLink-inline">
                  <span
                    v-safe-chat-html="part.originalUrlText"
                    class="u-colorPrimary" />
                  <SvgIcon
                    tiny
                    type="chat_link"
                    class="u-marginLeft1" />
                </KLink>
              </template>
            </div>
          </div>
        </template>
      </template>
      <MessageReactions :message="message" />
      <div
        v-if="messageTime"
        v-popper="{ content: messageFullTime }"
        class="c-chat__messageTime t-caption1">
        {{ messageTime }}
      </div>
      <SvgIcon
        v-if="message.metadata?.state === $constants.CHAT.STATES.ERROR && isMe(message.senderDisplayName)"
        class="c-chat__messageStateIndicator -error"
        tiny
        type="chat_info_error" />
      <SvgIcon
        v-if="message.metadata?.state === $constants.CHAT.STATES.SENT && !isMessageRead && isMe(message.senderDisplayName)"
        class="c-chat__messageStateIndicator"
        small
        type="chat_message_sent" />
      <SvgIcon
        v-if="isMessageRead && isMe(message.senderDisplayName) && message.metadata?.state !== $constants.CHAT.STATES.ERROR"
        class="c-chat__messageStateIndicator"
        small
        type="chat_message_read" />
    </div>
    <div
      v-if="isTranslatable"
      class="s-message__translate t-caption1 u-marginTop1">
      <template v-if="translatedPlainText && showTranslation">
        <button
          type="button"
          class="o-unstyled s-message__translateButton"
          @click="showOriginal">
          {{ $t('chat.ai_translated_show_original') }}
        </button>
      </template>
      <template v-else>
        <template v-if="isTranslatingManually">
          <LoadingSpinner small />
        </template>
        <template v-else>
          <button
            type="button"
            class="o-unstyled s-message__translateButton"
            @click="translateMessage(true)">
            {{ $t('chat.translate') }}
          </button>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { useTextDirection } from '@vueuse/core'
import { format } from 'date-fns'
import { find as findLinks } from 'linkifyjs'
import {
  computed,
  watch,
  ref,
  onMounted,
  onUnmounted,
} from 'vue'

import KLink from '@/components/KLink.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import constants from '@/constants'
import api from '@/logic/api'
import helpers from '@/logic/helpers'
import { useChatAttachmentsStore } from '@/modules/chat/chatAttachmentsStore'
import { useChatSearchMessagesHistoryStore } from '@/modules/chat/chatSearchMessagesHistoryStore'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import MessageAttachment from '@/modules/chat/components/MessageAttachment.vue'
import MessageContextMenu from '@/modules/chat/components/MessageContextMenu.vue'
import MessageReactions from '@/modules/chat/components/MessageReactions.vue'
import ReplyPreview from '@/modules/chat/components/ReplyPreview.vue'
import { useSwipeToReply } from '@/modules/chat/composables/useSwipeToReply'
import { sanitizeText, splitTextByCustomEmoji, sanitizeMessageContent } from '@/modules/chat/logic/chatMessageContent'
import { translateMessageForDisplay, normalizeChatMessageLanguage } from '@/modules/chat/logic/chatMessageTranslation'
import { getParticipantDisplayName } from '@/modules/chat/logic/chatThread'
import { countUnicodeEmojis, isTextOnlyUnicodeEmojis } from '@/modules/chat/logic/emojis'
import { canCancelUploadMessage } from '@/modules/chat/utils/canCancelUploadMessage'
import store from '@/store'

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['delete'])

const chatStore = useChatStore()
const chatThreadStore = useChatThreadStore(chatStore.chatThread.azure_chat_thread_id)
const chatAttachmentsStore = useChatAttachmentsStore(chatStore.chatThread.azure_chat_thread_id)

const messageRef = ref(null)
const contextMenuRef = ref(null)
const showContextMenuTrigger = ref(false)
const isContextMenuOpen = computed(() => {
  return contextMenuRef.value?.isMenuOpen?.value
    || contextMenuRef.value?.isToolbarHovered?.value
    || false
})
const isDeleting = ref(false)
const textDirection = useTextDirection()

const repliedToMessage = computed(() => {
  if (!props.message.metadata?.replyToMessageId) {
    return null
  }
  const messageId = props.message.metadata.replyToMessageId.toString()
  return chatThreadStore.repliedToMessages[messageId] || null
})

// Long press detection for mobile
let longPressTimer = null
const longPressTriggered = ref(false)
const LONG_PRESS_DURATION = 500 // ms

function isMe(userId) {
  return parseInt(userId) === store.getters['auth/userId']
}

// Check if this message is an in-progress upload that can be canceled by the sender
const canCancelUpload = computed(() => {
  return canCancelUploadMessage(props.message, store.getters['auth/userId'])
})

async function handleCancelUpload() {
  const fileId = chatAttachmentsStore.getFileIdByMessageId(props.message.id)
  if (!fileId) {
    return
  }
  await chatAttachmentsStore.cancelUploadOrDeleteMessage(fileId, props.message.id)
}
const isReply = computed(() => {
  return !!props.message.metadata?.replyToMessageId
})

const replyUsername = computed(() => {
  if (!repliedToMessage.value || repliedToMessage.value.deleted) {
    return ''
  }
  const senderId = parseInt(repliedToMessage.value.senderDisplayName)
  if (senderId === store.getters['auth/userId']) {
    return api.i18n.t('chat.you')
  }
  return getParticipantDisplayName(chatStore.chatThread)
})

async function fetchRepliedToMessageData() {
  if (!isReply.value) {
    return
  }
  const messageId = props.message.metadata.replyToMessageId
  await chatThreadStore.fetchRepliedToMessage(messageId)
}

function handleClickOutside(event) {
  if (!helpers.isTouchDevice()) {
    return
  }
  if (!showContextMenuTrigger.value) {
    return
  }
  // Check if click is outside this message
  if (messageRef.value && !messageRef.value.contains(event.target)) {
    showContextMenuTrigger.value = false
  }
}

onMounted(() => {
  fetchRepliedToMessageData()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
  }
  document.removeEventListener('click', handleClickOutside)
})

const canShowContextMenu = computed(() => {
  // Show context menu for all successfully sent messages (own and others)
  if (props.message.metadata?.state === constants.CHAT.STATES.ERROR) {
    return false
  }
  if (props.message.metadata?.state === constants.CHAT.STATES.SENDING) {
    return false
  }
  return true
})

const canDeleteMessage = computed(() => {
  // Only own messages can be deleted
  return isMe(props.message.senderDisplayName)
})

const canReplyToMessage = computed(() => {
  // Can reply to any successfully sent message
  return canShowContextMenu.value
})

// Swipe-to-reply on touch devices
const isOwnMessage = computed(() => isMe(props.message.senderDisplayName))

function handleReplyRequest() {
  chatThreadStore.replyingToMessage = props.message
}

const {
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  swipeDistance,
  swipeProgress,
  isSwiping,
  isPastThreshold,
} = useSwipeToReply(handleReplyRequest, () => canReplyToMessage.value, () => isOwnMessage.value)

function onMouseEnter() {
  if (!helpers.isTouchDevice()) {
    showContextMenuTrigger.value = true
  }
}

function onMouseLeave() {
  if (!helpers.isTouchDevice()) {
    showContextMenuTrigger.value = false
  }
}

function onMessageClick(event) {
  // On mobile, show context menu on tap (but not if it was a swipe or long press)
  if (helpers.isTouchDevice() && canShowContextMenu.value) {
    // Don't trigger if this was a swipe gesture or long press
    if (isSwiping.value || longPressTriggered.value) {
      return
    }
    event.stopPropagation()
    showContextMenuTrigger.value = !showContextMenuTrigger.value
  }
}

function handleLongPressStart() {
  if (!helpers.isTouchDevice() || !canShowContextMenu.value) {
    return
  }
  longPressTriggered.value = false
  longPressTimer = setTimeout(() => {
    longPressTriggered.value = true
    showContextMenuTrigger.value = true
  }, LONG_PRESS_DURATION)
}

function handleLongPressEnd() {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

function handleLongPressCancel() {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  // Reset the flag after a short delay to allow click event to check it
  setTimeout(() => {
    longPressTriggered.value = false
  }, 50)
}

function resetDeletingState() {
  isDeleting.value = false
}

// Wrapper functions to combine swipe and long press handling
function handleTouchStartWrapper(event) {
  handleTouchStart(event)
  handleLongPressStart()
}

function handleTouchMoveWrapper(event) {
  handleTouchMove(event)
  // Cancel long press if user starts moving (swiping)
  if (isSwiping.value) {
    handleLongPressEnd()
  }
}

function handleTouchEndWrapper(event) {
  handleTouchEnd(event)
  handleLongPressCancel()
}

const swipeStyle = computed(() => {
  if (!isSwiping.value) {
    return {}
  }
  // Own messages swipe left (negative), other messages swipe right (positive)
  const direction = (isOwnMessage.value ? -1 : 1) * (textDirection.value === 'rtl' ? -1 : 1)
  return {
    transform: `translateX(${swipeDistance.value * direction}px)`,
    transition: 'none',
  }
})

const swipeResetStyle = computed(() => {
  if (isSwiping.value) {
    return {}
  }
  return {
    transition: 'transform 0.2s ease-out',
  }
})

/**
 * Swipe indicator scale: starts at 0.5 (half size) and grows to 1 (full size)
 * We use 0.5 as minimum so the icon is immediately visible when swiping starts,
 * rather than starting invisible at scale 0
 */
const swipeIndicatorScale = computed(() => {
  const minScale = 0.5
  const maxScale = 1
  return minScale + (swipeProgress.value * (maxScale - minScale))
})

/**
 * Swipe indicator opacity: starts at 0.3 and grows to 1 (fully opaque)
 * We use 0.3 as minimum so the icon is visible from the start,
 * but still has room to become more prominent as the swipe progresses
 */
const swipeIndicatorOpacity = computed(() => {
  const minOpacity = 0.3
  const maxOpacity = 1
  return minOpacity + (swipeProgress.value * (maxOpacity - minOpacity))
})

function handleDeleteRequest() {
  if (isDeleting.value) {
    return
  }
  isDeleting.value = true
  emit('delete', props.message, resetDeletingState)
}

const messageTime = computed(() => {
  if (!props.message.createdOn) return null

  const date = new Date(props.message.createdOn)
  return format(date, 'HH:mm')
})

const messageFullTime = computed(() => {
  if (!props.message.createdOn) return null

  const date = new Date(props.message.createdOn)
  return format(date, 'dd.MM.yyyy HH:mm')
})

const isMessageRead = computed(() => {
  if (!chatThreadStore.partnerReadReceipt) {
    return false
  }

  if (+chatThreadStore.partnerReadReceipt.chatMessageId >= +props.message.id) {
    return true
  }

  return false
})

const isAttachment = computed(() => {
  if (!props.message.metadata?.file_id) {
    return false
  }

  // Images have a base64 preview before the message content is available.
  if (props.message.metadata?.preview_base64) {
    return true
  }

  const content = props.message.content
  if (content instanceof Blob || (typeof content === 'string' && content.startsWith('blob'))) return true

  try {
    // Assign to _ to satisfy 'no-new' for side effects, as we only care about the try/catch
    // eslint-disable-next-line no-unused-vars
    const _ = new URL(content)
    return true
  } catch {
    return false
  }
})

const isImageAttachment = computed(() => {
  return isAttachment.value && parseInt(props.message.metadata?.file_type, 10) === constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE
})

const chatSearchMessagesHistoryStore = useChatSearchMessagesHistoryStore()

const showLoaderForReceiver = computed(() => {
  if (isMe(props.message.senderDisplayName)) {
    return false
  }
  if (props.message.metadata?.state !== constants.CHAT.STATES.SENDING) {
    return false
  }
  // Images have a preview, so we show the actual attachment which will render the preview by default.
  if (props.message.metadata?.preview_base64) {
    return false
  }
  return true
})

// Collect user IDs (me + chat participants) for entity access checks
const userIdsForAccessCheck = computed(() => {
  const userIds = new Set()
  const chatThread = chatStore.chatThread

  userIds.add(store.getters['auth/userId'])
  userIds.add(chatThread.participant.user_id)

  return Array.from(userIds)
})

// Always resolve the current message from the store by id to avoid stale references
const storeMessage = computed(() => {
  return chatThreadStore.sortedMessages.find((m) => Number(m.id) === Number(props.message.id)) || props.message
})

const translatedPlainText = computed({
  get() {
    return storeMessage.value?.translatedText || null
  },
  set(value) {
    chatThreadStore.updateMessageByAzureId(props.message.id, { translatedText: value })
  },
})
const showTranslation = computed({
  get() {
    return !!storeMessage.value.showTranslation
  },
  set(value) {
    chatThreadStore.updateMessageByAzureId(props.message.id, { showTranslation: !!value })
  },
})
// Tracks if a manual translation is currently in progress
const isTranslatingManually = ref(false)

// If true, user has explicitly chosen to show original for this message.
const wasTranslatedManually = computed({
  get() {
    return !!storeMessage.value?.wasTranslatedManually
  },
  set(value) {
    chatThreadStore.updateMessageByAzureId(props.message.id, { wasTranslatedManually: !!value })
  },
})
const hasTranscript = computed(() => {
  return !!props.message.metadata?.transcript
})

const getMessageContent = () => {
  return sanitizeMessageContent(props.message.content)
}

const isOnlyEmojiMessage = computed(() => {
  if (isAttachment.value === true) {
    return false
  }
  if (!props.message.content) {
    return false
  }
  const parts = splitTextByCustomEmoji(getMessageContent())
  if (!parts.length) {
    return false
  }
  const hasCustomEmoji = parts.some((p) => p.type === 'emoji')
  const textOnly = parts.filter((p) => p.type === 'text').map((p) => p.content).join('')
  const textStripped = textOnly.replace(/<br\s*\/?>/gi, '').trim()

  // Only custom emojis, no other visible text
  if (hasCustomEmoji && textStripped === '') {
    return true
  }

  // Only Unicode emojis, no other visible text
  return isTextOnlyUnicodeEmojis(textOnly)
})

// Count total emojis (custom + UTF-8) in the message
const emojiCount = computed(() => {
  if (!props.message.content) {
    return 0
  }
  const parts = splitTextByCustomEmoji(getMessageContent())
  // Count custom emojis from parts
  const customEmojiCount = parts.filter((p) => p.type === 'emoji').length
  // Count UTF-8 emojis from text parts
  const textOnly = parts.filter((p) => p.type === 'text').map((p) => p.content).join('')
  const unicodeEmojiCount = countUnicodeEmojis(textOnly)
  return customEmojiCount + unicodeEmojiCount
})

// Display emojis at larger size when message is emoji-only and has 5 or fewer emojis
const shouldDisplayLargeEmojis = computed(() => {
  return isOnlyEmojiMessage.value && emojiCount.value <= 5
})

const isTranslatable = computed(() => {
  // Only translate messages from the other user
  if (isMe(props.message.senderDisplayName)) {
    return false
  }

  const msgLang = props.message?.metadata?.language
  if (!msgLang) {
    return false
  }

  const normalizedMsgLang = normalizeChatMessageLanguage(msgLang)
  const normalizedUserLocale = normalizeChatMessageLanguage(store.getters['auth/locale'])

  // Attachment with transcript: allow translate
  if (isAttachment.value === true) {
    if (!hasTranscript.value) {
      return false
    }
    if (normalizedMsgLang === normalizedUserLocale) {
      return false
    }
    return true
  }
  // Regular text message
  if (normalizedMsgLang === normalizedUserLocale) {
    return false
  }
  if (!props.message.content) {
    return false
  }
  if (isOnlyEmojiMessage.value) {
    return false
  }
  return true
})

function maybePromptEnableAutoTranslate(isManual) {
  if (!isManual) {
    return
  }
  if (chatStore.chatThread && chatStore.chatThread.auto_translate_enabled !== true) {
    chatThreadStore.triggerAutoTranslatePromptForThread()
  }
}

async function translateMessage(isManual = false) {
  // If we already have a translation cached, just show it.
  if (translatedPlainText.value) {
    if (wasTranslatedManually.value && !isManual) {
      return
    }
    showTranslation.value = true
    if (isManual) {
      wasTranslatedManually.value = false
      maybePromptEnableAutoTranslate(true)
    }
    return
  }

  try {
    if (isManual) {
      isTranslatingManually.value = true
    }
    const translation = await translateMessageForDisplay({
      chatThreadStore,
      message: props.message,
      targetLanguage: store.getters['auth/locale'],
    })

    if (translation) {
      translatedPlainText.value = translation
      if (wasTranslatedManually.value && !isManual) {
        return
      }
      showTranslation.value = true
      if (isManual) {
        wasTranslatedManually.value = false
        // After a manual translation, offer enabling auto-translate for this thread
        maybePromptEnableAutoTranslate(true)
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
  } finally {
    if (isManual) {
      isTranslatingManually.value = false
    }
  }
}

function showOriginal() {
  showTranslation.value = false
  wasTranslatedManually.value = true
}

const getLink = (link) => {
  if (!link) return ''
  const temp = document.createElement('div')
  temp.innerHTML = link
  let text = temp.textContent.trim()

  if (!/^https?:\/\//i.test(text)) text = `https://${text}`

  try {
    return new URL(text).href
  } catch {
    return ''
  }
}

const messageParts = computed(() => {
  const plainText = translatedPlainText.value && showTranslation.value
    ? translatedPlainText.value
    : getMessageContent()

  const matchedLinks = findLinks(plainText, 'url')
  if (!matchedLinks || matchedLinks.length === 0) {
    return splitTextByCustomEmoji(plainText)
  }

  const messageContentParts = []
  let cursor = 0
  matchedLinks.forEach((m) => {
    if (m.start > cursor) {
      messageContentParts.push({ type: 'text', content: plainText.slice(cursor, m.start) })
    }
    const query = chatSearchMessagesHistoryStore?.searchQuery || ''
    const highlightRegex = query ? new RegExp(query, 'gi') : null
    const highlightedText = highlightRegex ? m.value.replace(highlightRegex, (txt) => `<span class="c-chat__keyword">${txt}</span>`) : m.value
    messageContentParts.push({ type: 'link', url: getLink(m.value), originalUrlText: highlightedText })
    cursor = m.end
  })
  if (cursor < plainText.length) {
    messageContentParts.push({ type: 'text', content: plainText.slice(cursor) })
  }

  // After link extraction, split text parts for custom emojis and flatten
  const flattened = []
  for (const part of messageContentParts) {
    if (part.type === 'text') {
      flattened.push(...splitTextByCustomEmoji(part.content))
    } else {
      flattened.push(part)
    }
  }

  return flattened
})

// Auto-translate when the thread setting is enabled
function maybeAutoTranslate() {
  if (chatStore.chatThread?.auto_translate_enabled !== true) {
    return
  }
  if (wasTranslatedManually.value) {
    return
  }
  if (!isTranslatable.value) {
    return
  }
  if (translatedPlainText.value && showTranslation.value) {
    return
  }
  translateMessage()
}

// Trigger auto-translate whenever anything changes that affects auto-translate
watch([
  () => chatStore.chatThread?.auto_translate_enabled,
  () => storeMessage.value && storeMessage.value.id,
  () => isTranslatable.value,
], () => {
  maybeAutoTranslate()
}, { immediate: true })

const transcriptHtml = computed(() => {
  if (!props.message.metadata?.transcript) {
    return ''
  }

  if (isAttachment.value && showTranslation.value && translatedPlainText.value) {
    return sanitizeText(translatedPlainText.value)
  }

  return sanitizeText(props.message.metadata.transcript)
})

</script>

<style scoped lang="scss">
@import '@/assets/scss/config';
.s-message {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;

  &.-showContextMenu {
    :deep(.s-messageContextMenu) {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.s-message--swiping {
    user-select: none;
  }
}

.s-message__swipeIndicator {
  position: absolute;
  left: -$spacing-6;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: $spacing-5;
  height: $spacing-5;
  border-radius: 50%;
  background-color: $color-light-gray2;
  color: $color-dark_medium_emphasis;
  transition: background-color 0.15s ease-out, color 0.15s ease-out;

  &.-active {
    background-color: $color-primary;
    color: $color-white;
  }

  // Own messages: indicator on the right (message swipes left)
  &.-ownMessage {
    left: auto;
    right: -$spacing-6;
  }
}

[dir='rtl'] {
  .s-message__swipeIndicator {
    left: auto;
    right: -$spacing-6;

    // RTL + own message: indicator on the left
    &.-ownMessage {
      left: -$spacing-6;
      right: auto;
    }
  }
}

.s-messageContent {
  white-space: pre-line;
  word-break: break-word;

  /* We use this pseudo element to reserve space for the message meta */
  &::after {
    content: '';
    float: right;
    width: 60px;
    height: 18px;
  }
}

.s-message__translate {
  align-self: flex-start;
  padding-left: $spacing-2;
}

.s-message__translateButton {
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
}

.s-chat__audioTranscript {
  color: $color-gray;
}

.c-chat__messageLink-inline {
  display: block;
}

[dir='rtl'] {
  .s-messageContent {
    &::after {
      float: left;
    }
  }
}
</style>
